// Competitions embeddables — pure formatting helpers (UI-SPEC §22, verbatim
// sample formats). Everything here is deterministic and unit-testable; no
// locale machinery — numbers keep the spec's fixed shapes.

export const MINUS = "−"; // U+2212, the spec's real minus sign for Diff

// +42 / 0 / −3 (U+2212, never the ASCII hyphen) — UI-SPEC §7 / §22.
export const formatDiff = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  if (n > 0) return `+${n}`;
  if (n < 0) return `${MINUS}${Math.abs(n)}`;
  return "0";
};

// "62:20" — UI-SPEC §22 (Tore column).
export const formatGoals = (goalsFor, goalsAgainst) => {
  if (goalsFor == null || goalsAgainst == null) return null;
  return `${goalsFor}:${goalsAgainst}`;
};

// "3 : 0" / "4 : 3" — spaces around the colon (UI-SPEC §22; also agg.).
export const formatScore = (home, away) => {
  if (home == null || away == null) return null;
  return `${home} : ${away}`;
};

// [[2,1],[2,2]] -> "2–1 · 2–2" (en-dash U+2013, middle dot) — UI-SPEC §22.
export const formatLegs = (legs) => {
  if (!Array.isArray(legs) || legs.length === 0) return null;
  const parts = legs
    .filter((leg) => Array.isArray(leg) && leg[0] != null && leg[1] != null)
    .map((leg) => `${leg[0]}–${leg[1]}`);
  return parts.length ? parts.join(" · ") : null;
};

// "5–4" (en-dash) for the penalty suffix line — UI-SPEC §13.
export const formatPenaltyScore = (penalties) => {
  if (!penalties || penalties.home == null || penalties.away == null) return null;
  return `${penalties.home}–${penalties.away}`;
};

// Monogram fallback text for crest placeholders (never an invented name).
export const teamInitials = (team) => {
  const src = ((team && (team.short_name || team.name)) || "").trim();
  if (!src) return "";
  const parts = src.split(/\s+/).filter(Boolean).slice(0, 2);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

// 1 -> "A", 2 -> "B" … (TheSports `group`/`group_num`: "1-A, 2-B and so on").
export const groupLetter = (number) => {
  const n = Number(number);
  if (!Number.isFinite(n) || n < 1 || n > 26) return "";
  return String.fromCharCode(64 + n);
};

// Winner side of a bracket matchup when the feed doesn't say it outright:
// penalties decide first (score stays untouched — suffix rule), then the
// aggregate, then the single-match score. null = undecided.
export const deriveWinner = (matchup) => {
  if (!matchup) return null;
  if (matchup.winner === "home" || matchup.winner === "away") return matchup.winner;
  const pens = matchup.penalties;
  if (pens && pens.home != null && pens.away != null && pens.home !== pens.away) {
    return pens.home > pens.away ? "home" : "away";
  }
  const agg = matchup.aggregate;
  if (Array.isArray(agg) && agg[0] != null && agg[1] != null && agg[0] !== agg[1]) {
    return agg[0] > agg[1] ? "home" : "away";
  }
  if (
    matchup.homeScore != null &&
    matchup.awayScore != null &&
    matchup.homeScore !== matchup.awayScore
  ) {
    return matchup.homeScore > matchup.awayScore ? "home" : "away";
  }
  return null;
};
